import {
  DocumentData,
  Query,
  QueryConstraint,
  collection,
  doc,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase/firebase";
import { collectionData, docData } from "rxfire/firestore";
import { Observable, map, of } from "rxjs";
import DataTransformationService from "./data-transformation.service";
import { Placement } from "../models/placement";
import { Tenant } from "../models/tenant";
import { Experience } from "../models/experience";
import { Brand } from "../models/brand";

function getPlacementUrl(
  tenant: Tenant,
  brand: Brand,
  placement: Placement
): string {
  if (!brand.hostname && !tenant.hostname)
    throw new Error("Experience hostname has not been configured.");
  if (!placement?.id) throw new Error("Placement does not have a valid ID.");

  return `https://${brand.hostname || tenant.hostname}/${placement.id}`;
}

function generateId() {
  return doc(collection(db, "placements")).id;
}

function getOne(id: string): Observable<Placement | undefined> {
  const docRef = doc(db, `placements/${id}`);
  return docData(docRef).pipe(
    map((firestoreDocument) =>
      DataTransformationService.convertTimestampsToIsoString(firestoreDocument)
    )
  );
}

function getAllByExperience(
  tenantId: string,
  brandId: string,
  experienceId: string
): Observable<Placement[]> {
  const collectionRef = collection(db, "placements");

  const queryConstraint: QueryConstraint[] = [
    where("tenantId", "==", tenantId),
    where("brandId", "==", brandId),
    where("experienceId", "==", experienceId),
    where("deleted", "==", false),
  ];

  const collectionQuery: Query<DocumentData> = query(
    collectionRef,
    ...queryConstraint
  );

  return collectionData(collectionQuery).pipe(
    map((collection) => {
      //sort by order
      collection.sort(
        (a, b) => (a.order ? a.order : 0) - (b.order ? b.order : 0)
      );
      //convert timestamps to Date
      collection = collection.map((firestoreDocument) =>
        DataTransformationService.convertTimestampsToIsoString(
          firestoreDocument
        )
      );
      return collection;
    })
  );
}

async function saveOne(
  placement: Partial<Placement>,
  isNew: boolean
): Promise<any> {
  try {
    if (isNew) {
      placement.createdDate = serverTimestamp();
      placement.createdByUserAccountId = auth.currentUser.uid;
    } else {
      placement.updatedDate = serverTimestamp();
      placement.updatedByUserAccountId = auth.currentUser.uid;
    }

    if (placement.deleted == undefined) placement.deleted = false;

    placement =
      DataTransformationService.convertDatesToFirestoreTimestamp(placement);

    let docRef = doc(db, `placements/${placement.id}`);
    await setDoc(docRef, placement, { merge: true });
    return;
  } catch (err) {
    throw err;
  }
}

async function deleteOne(placementId: string): Promise<any> {
  try {
    let docRef = doc(db, `placements/${placementId}`);
    const documentToDelete = {
      deleted: true,
      deletedDate: serverTimestamp(),
      updatedDate: serverTimestamp(),
      updatedByUserAccountId: auth.currentUser.uid,
    };
    await setDoc(docRef, documentToDelete, { merge: true });
    return;
  } catch (err) {
    throw err;
  }
}

const PlacementService = {
  generateId,
  getOne,
  saveOne,
  deleteOne,
  getAllByExperience,
  getPlacementUrl,
};

export default PlacementService;
